import { useMutation, useQuery } from "@tanstack/react-query";
import {
  operatorEndpoints,
  sharedEndpoints,
  authEndpoints,
} from "@src/services/endpoints";
import SignolUtils from "@src/utils/signolUtils";
import { queryClient } from "@src/utils/queryClient";
import { trackError } from "../../utils/userActivityTracker";
import { use } from "react";

const {
  getGoalSummaries,
  getLatestEvents,
  getBestEvent,
  getImpact,
  getCustomerCommitments,
  getEvents,
  getEventById,
  getIncompleteEvents,
  getBaselines,
  submitProfileUpdate,
  saveSelfReport,
  updateEventComment,
  getNotifications,
  readNotification,
} = operatorEndpoints;

const MILLISECONDS_IN_TEN_MINUTES = 10 * 60 * 1000;

// OperatorContainer inner components will rely on the QueryClient CACHE (staled data) as primary source of state-sync
const OperatorHooks = {
  // custom hooks below...

  useOperatorGoals: (operatorId, industry) =>
    useQuery({
      queryKey: ["GOAL_SUMMARIES", operatorId, industry],
      queryFn: () => getGoalSummaries(operatorId, industry),
      enabled: !!operatorId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES, // cached for 10 min (this is not going to change during user daily session)
      select: (data) => ({
        goalsData: data.goalSummaries,
        currentPeriod: data.goalSummaries[0] ?? null,
      }),
    }),

  useOperatorLatestEvents: (operatorId) =>
    useQuery({
      queryKey: ["LATEST_EVENTS", operatorId],
      queryFn: () => getLatestEvents(4),
      enabled: !!operatorId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES, // cached for 10 min (this is not going to change during user daily session)
      select: (data) => ({
        latestEvents: data.events,
      }),
    }),

  useOperatorBestEvent: (operatorId) =>
    useQuery({
      queryKey: ["BEST_EVENT", operatorId],
      queryFn: () => getBestEvent(operatorId),
      enabled: !!operatorId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES, // cached for 10 min (this is not going to change during user daily session)
      select: (data) => ({
        bestEvent: data.bestEvent,
      }),
    }),

  useOperatorEvents: (from, to, operatorId, industry) =>
    useQuery({
      queryKey: ["EVENTS", operatorId, from, to, industry],
      queryFn: () => getEvents(from, to, operatorId, industry),
      enabled: !!operatorId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES, // cached for 10 min (this is not going to change during user daily session)
      select: (data) => ({
        events: data.events,
      }),
    }),

  useOperatorEventById: (operatorId, eventId) =>
    useQuery({
      queryKey: ["EVENTS", operatorId, eventId],
      queryFn: () => getEventById(operatorId, eventId),
      enabled: !!operatorId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES, // cached for 10 min (this is not going to change during user daily session)
      select: (data) => ({
        event: data.event,
      }),
    }),

  useOperatorIncompleteEvents: (operatorId, industry) =>
    useQuery({
      queryKey: ["INCOMPLETE_EVENTS", operatorId, industry],
      queryFn: () => getIncompleteEvents(operatorId, industry),
      enabled: !!operatorId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES, // cached for 10 min (this is not going to change during user daily session)
      select: (data) => ({
        incompleteEvents: data.events,
      }),
    }),

  useOperatorImpact: (operatorId, industry) =>
    useQuery({
      queryKey: ["IMPACT", operatorId, industry],
      queryFn: () => getImpact(operatorId, industry),
      enabled: !!operatorId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES, // cached for 10 min (this is not going to change during user daily session)
      select: (data) => ({
        impact: data,
        personalAllTimeTotals:
          SignolUtils.buildPersonalImpactTotalsOverview(data),
      }),
    }),

  useCustomerCommitments: (operatorId) =>
    useQuery({
      queryKey: ["COMMITMENTS", operatorId],
      queryFn: () => getCustomerCommitments(operatorId),
      enabled: !!operatorId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES, // cached for 10 min (this is not going to change during user daily session)
      select: (data) => ({
        customerCommitments: data.customerCommitments,
      }),
    }),

  //TODO - Upgrade to useInfiniteQuery --> required an endpoint response change in notificationsService --> continuous improvement
  useOperatorNotifications: (operatorCode, role, subtypes, offset, limit) =>
    useQuery({
      queryKey: ["NOTIFICATIONS", operatorCode, role, subtypes, offset, limit],
      queryFn: () =>
        getNotifications({ operatorCode, role, subtypes, offset, limit }),
      enabled: !!operatorCode,
      staleTime: MILLISECONDS_IN_TEN_MINUTES, // cached for 10 min (this is not going to change during user daily session)
      select: (data) => {
        return {
          notifications: data?.data,
        };
      },
    }),

  useReadNotification: (callback) =>
    useMutation({
      mutationFn: (data) => {
        console.log("Reading notification:", data);
        return readNotification(data);
      },
      onSuccess: (data) => {
        const readNotificationId = data?.data;
        queryClient.setQueryData(["NOTIFICATIONS"], (oldData) => {
          if (!oldData?.notifications) return oldData;

          const updated = oldData.notifications.map((n) =>
            n.id === readNotificationId ? { ...n, isRead: true } : n,
          );

          return { notifications: updated };
        });

        console.log("Notification read successfully", data);
        if (callback) {
          callback(data);
        }
      },
      onError: (error, variables, context) => {
        // handle error, e.g., show an error message
        console.error("Error reading notification", error);
        trackError(error, "operator-useReadNotification", {
          variables,
          context,
        });
      },
    }),

  useOperatorBaselines: (operatorId, industry) =>
    useQuery({
      queryKey: ["EVENTS", operatorId, industry],
      queryFn: () => getBaselines(operatorId, industry),
      enabled: !!operatorId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES, // cached for 10 min (this is not going to change during user daily session)
      select: (data) => ({
        baselines: data.baselines,
      }),
    }),

  useBehaviourExclusions: (operatorId) =>
    useQuery({
      queryKey: ["BEHAVIOUR_EXCLUSIONS", operatorId],
      queryFn: () => sharedEndpoints.fetchBvrExclusions(),
      enabled: !!operatorId,
      staleTime: MILLISECONDS_IN_TEN_MINUTES,
      select: (
        /** @type {Record<string, Array<{field_name: string, values: string[]}>} */
        data,
      ) => {
        if (data.error) {
          console.error("api error:", data);
          throw Error(data);
        }
        return {
          exclusions: data,
        };
      },
    }),

  useSubmitOperatorProfile: (callback) =>
    useMutation({
      mutationFn: (data) => submitProfileUpdate(data),
      onSuccess: (data) => {
        // handle success, e.g., show a toast or update state
        console.log("Profile updated successfully", data);
        if (callback) {
          callback(data, true);
        }
      },
      onError: (error, variables, context) => {
        // handle error, e.g., show an error message
        console.error("Error updating profile", error);
        trackError(error, "operator-useSubmitOperatorProfile", {
          variables,
          context,
        });
        if (callback) {
          callback(null, false);
        }
      },
    }),

  useSaveSelfReport: (callback, operatorId, industry) =>
    useMutation({
      mutationFn: (data) => saveSelfReport(data),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["INCOMPLETE_EVENTS", operatorId, industry],
        });
        if (callback) {
          callback(true);
        }
        console.log("Self report saved successfully", data);
      },
      onError: (error, variables, context) => {
        if (callback) {
          callback(false);
        }
        console.error("Error saving self report", error);
        trackError(error, "operator-useSaveSelfReport", { variables, context });
      },
    }),

  useUpdateEventComment: (callback) =>
    useMutation({
      mutationFn: (inputData) => updateEventComment(inputData),
      onSuccess: (data) => {
        // handle success, e.g., show a toast or update state
        console.log("Event comment updated successfully", data);
        if (callback) {
          callback(data);
        }
      },
      onError: (error, variables, context) => {
        // handle error, e.g., show an error message
        console.error("Error updating event comment", error);
        trackError(error, "operator-useUpdateEventComment", {
          variables,
          context,
        });
      },
    }),

  useSubmitSupportRequest: (callback) =>
    useMutation({
      mutationFn: (inputData) => sharedEndpoints.sendSupportRequest(inputData),
      onSuccess: (data) => {
        console.log("Support request sent successfully", data);
        // handle success, e.g., show a toast or update state
        if (callback) {
          callback(true);
        }
      },
      onError: (error, variables, context) => {
        console.error("Error sending support request", error);
        if (callback) {
          callback(false);
        }
        trackError(error, "operator-SupportRequest", { variables, context });
        // handle error, e.g., show an error message
      },
    }),

  // add more...

  // to invalidate cache, please see more: https://tanstack.com/query/v4/docs/framework/react/guides/invalidations-from-mutations
  // example: submit event comment in <OperatorEventDetailsPage/>
};

export default OperatorHooks;
